import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  Users, 
  CheckCircle2, 
  AlertCircle,
  AlertTriangle,
  Edit,
  Send,
  Download,
  Eye,
  PenTool,
  Pencil,
  Calendar,
  DollarSign,
  User,
  MessageSquare,
  X,
  UserPlus,
  Trash2,
  Plus,
  Activity,
  Package,
  Folder,
  Play,
  Pause,
  Timer,
  MoreHorizontal,
  HardDrive,
  Upload,
  ExternalLink,
  Link2,
  File,
  FileImage,
  FileVideo,
  FilePlus,
  Calculator,
  Ticket,
  Receipt,
  Copy,
  Ban,
  Archive,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Loader2,
  Mic,
  Square,
  Volume2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import BriefForm from '../../components/projects/BriefForm';
import WebsiteBriefForm from '../../components/projects/WebsiteBriefForm';
import DynamicBriefForm from '../../components/projects/DynamicBriefForm';
import BriefView from '../../components/projects/BriefView';
import ProjectChat from '../../components/chat/ProjectChat';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ChronoLauncher from '../../components/timer/ChronoLauncher';

// Inline Messages Panel Component
const ProjectMessagesPanel = ({ projectId, theme, user }) => {
  const { isClient, isAgency, isProjectManager, isTeamMember } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageTarget, setMessageTarget] = useState('all'); // 'all', 'team', 'client'
  const [showClientAlert, setShowClientAlert] = useState(false);
  const messagesEndRef = React.useRef(null);

  const canChooseTarget = isAgency || isProjectManager || isTeamMember;

  React.useEffect(() => {
    if (projectId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [projectId]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/messages`);
      let msgList = res.data || [];
      
      // Filter out action_request messages for clients
      if (isClient) {
        msgList = msgList.filter(msg => msg.message_type !== 'action_request');
      }
      
      // Filter messages based on user role and message visibility
      if (isTeamMember && !isAgency && !isProjectManager) {
        // Team members don't see client-only messages unless they are part of the conversation
        msgList = msgList.filter(msg => 
          msg.visibility !== 'client_only' || msg.sender_id === user?.id
        );
      }
      
      setMessages(msgList);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    
    // Show alert if sending to client
    if ((messageTarget === 'client' || messageTarget === 'all') && canChooseTarget) {
      setShowClientAlert(true);
      return;
    }
    
    await sendMessage();
  };

  const sendMessage = async () => {
    setSending(true);
    setShowClientAlert(false);
    try {
      await api.post(`/projects/${projectId}/messages`, {
        content: newMessage,
        message_type: 'text',
        visibility: messageTarget // 'all', 'team', 'client'
      });
      setNewMessage('');
      await fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getRoleColor = (role) => {
    if (role === 'agency') return 'bg-indigo-100 text-indigo-700';
    if (role === 'client') return 'bg-emerald-100 text-emerald-700';
    return 'bg-blue-100 text-blue-700';
  };

  const getRoleLabel = (role) => {
    if (role === 'agency') return 'Agence';
    if (role === 'client') return 'Client';
    return 'Équipe';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[500px]">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-[#64748b]">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun message pour le moment</p>
            <p className="text-xs mt-1">Soyez le premier à envoyer un message !</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === user?.id;
            // Get visibility label
            const getVisibilityLabel = (visibility) => {
              switch(visibility) {
                case 'team': return '👥 Équipe';
                case 'client': return '🏢 Client';
                default: return '🌐 Tous';
              }
            };
            const getVisibilityColor = (visibility) => {
              switch(visibility) {
                case 'team': return 'bg-blue-50 text-blue-600 border-blue-200';
                case 'client': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
                default: return 'bg-slate-50 text-slate-600 border-slate-200';
              }
            };
            return (
              <div
                key={msg.id}
                className={cn(
                  "flex gap-3",
                  isOwn ? "flex-row-reverse" : ""
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0",
                  msg.sender_role === 'agency' ? "bg-indigo-500" :
                  msg.sender_role === 'client' ? "bg-emerald-500" : "bg-blue-500"
                )}>
                  {msg.sender_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                
                {/* Message Content */}
                <div className={cn("max-w-[75%]", isOwn && "text-right")}>
                  <div className={cn("flex items-center gap-2 mb-1 flex-wrap", isOwn && "flex-row-reverse")}>
                    <span className="text-xs font-medium text-[#334155]">
                      {msg.sender_name}
                    </span>
                    <Badge className={cn("text-[10px] px-1.5 py-0", getRoleColor(msg.sender_role))}>
                      {getRoleLabel(msg.sender_role)}
                    </Badge>
                    {/* Visibility indicator - only show for agency/PM/team */}
                    {(isAgency || isProjectManager || isTeamMember) && msg.visibility && (
                      <span className={cn(
                        "text-[9px] px-1.5 py-0.5 rounded border",
                        getVisibilityColor(msg.visibility)
                      )}>
                        {getVisibilityLabel(msg.visibility)}
                      </span>
                    )}
                    <span className="text-[10px] text-[#94a3b8]">
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                  
                  <div className={cn(
                    "p-3 rounded-xl text-sm",
                    isOwn 
                      ? "bg-[#6366f1] text-white rounded-br-sm" 
                      : "bg-[#f1f5f9] text-[#334155] rounded-bl-sm"
                  )}>
                    {msg.content}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Area */}
      <div className="border-t border-[#e2e8f0] pt-4">
        {/* Target Selector for agency/project manager/team */}
        {canChooseTarget && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-slate-500">Envoyer à :</span>
            <div className="flex gap-1">
              <button
                onClick={() => setMessageTarget('all')}
                className={cn(
                  "px-2 py-1 text-xs rounded-md transition-colors",
                  messageTarget === 'all' 
                    ? "bg-indigo-100 text-indigo-700 font-medium" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
              >
                Tous
              </button>
              <button
                onClick={() => setMessageTarget('team')}
                className={cn(
                  "px-2 py-1 text-xs rounded-md transition-colors",
                  messageTarget === 'team' 
                    ? "bg-blue-100 text-blue-700 font-medium" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
              >
                Équipe seule
              </button>
              <button
                onClick={() => setMessageTarget('client')}
                className={cn(
                  "px-2 py-1 text-xs rounded-md transition-colors",
                  messageTarget === 'client' 
                    ? "bg-emerald-100 text-emerald-700 font-medium" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
              >
                Client seul
              </button>
            </div>
          </div>
        )}

        {/* Client Alert Modal */}
        {showClientAlert && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Attention : Message visible par le client</p>
                <p className="text-xs text-amber-600 mt-1">Ce message sera visible par le client du projet.</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setShowClientAlert(false)}>
                    Annuler
                  </Button>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={sendMessage}>
                    Confirmer l'envoi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Écrivez votre message..."
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button 
            onClick={handleSend}
            disabled={sending || !newMessage.trim()}
            style={{ backgroundColor: theme.colors?.accent }}
            className="rounded-full"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Status configuration for project display
const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  pending_brief: { label: 'Brief en attente', color: 'bg-yellow-100 text-yellow-700' },
  brief_sent: { label: 'Brief envoyé', color: 'bg-blue-100 text-blue-700' },
  brief_signed: { label: 'Brief validé', color: 'bg-green-100 text-green-700' },
  in_progress: { label: 'En cours', color: 'bg-purple-100 text-purple-700' },
  review: { label: 'En revue', color: 'bg-orange-100 text-orange-700' },
  delivered: { label: 'Livré', color: 'bg-teal-100 text-teal-700' },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
  on_hold: { label: 'En pause', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
};

// Helper function to format hours as "XhXXmin"
const formatHoursToHM = (hours) => {
  if (!hours || isNaN(hours)) return '0h00min';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, '0')}min`;
};

// Helper function to format hours for input (round to 2 decimals)
const formatHoursForInput = (hours) => {
  if (!hours || isNaN(hours)) return '';
  return parseFloat(hours).toFixed(2);
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isAgency, isClient, isTeamMember, isProjectManager } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [brief, setBrief] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [finances, setFinances] = useState({
    totalCost: 0,
    teamCost: 0,
    expensesCost: 0,
    margin: 0,
    margin_percentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [showBriefForm, setShowBriefForm] = useState(false);
  const [editBriefData, setEditBriefData] = useState({});
  const [savingBrief, setSavingBrief] = useState(false);
  
  // Audio recording for brief edit
  const [editAudioBlob, setEditAudioBlob] = useState(null);
  const [editAudioUrl, setEditAudioUrl] = useState(null);
  const [editAudioTranscript, setEditAudioTranscript] = useState('');
  const [isRecordingEdit, setIsRecordingEdit] = useState(false);
  const [isTranscribingEdit, setIsTranscribingEdit] = useState(false);
  const editMediaRecorderRef = useRef(null);
  const editChunksRef = useRef([]);
  
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [estimation, setEstimation] = useState({ hours: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [clientWallet, setClientWallet] = useState(null);
  const [briefExpanded, setBriefExpanded] = useState(false); // Brief collapsed by default when signed
  
  // Team management states
  const [showAssignManagerModal, setShowAssignManagerModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  const [projectManager, setProjectManager] = useState(null);
  const [assignedTeamDetails, setAssignedTeamDetails] = useState([]);
  
  // Task management states
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    assigned_to: '',
    estimated_hours: '',
    deadline: '',
    max_versions: 3
  });
  
  // Timeline/Activity states
  const [projectActivity, setProjectActivity] = useState([]);
  
  // Edit project states
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({
    name: '',
    description: '',
    estimated_hours: '',
    deadline: '',
    status: ''
  });
  
  // Google Drive and Files states
  const [showGoogleDriveModal, setShowGoogleDriveModal] = useState(false);
  const [googleDriveUrl, setGoogleDriveUrl] = useState('');
  const [googleDrivePassword, setGoogleDrivePassword] = useState('');
  const [showUploadFileModal, setShowUploadFileModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  
  // Tickets related to project
  const [projectTickets, setProjectTickets] = useState([]);
  const [projectInvoices, setProjectInvoices] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState(null);
  const [sendingPaymentRequest, setSendingPaymentRequest] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProjectData();
    }
  }, [projectId, user?.id]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, briefRes, tasksRes, scheduleRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/briefs/project/${projectId}`).catch(() => ({ data: null })),
        api.get(`/tasks/project/${projectId}`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/payment-schedule`).catch(() => ({ data: null }))
      ]);
      
      setProject(projectRes.data);
      setBrief(briefRes.data);
      setTasks(tasksRes.data || []);
      setPaymentSchedule(scheduleRes.data);

      // Fetch invoices for this project
      const invoicesRes = await api.get(`/projects/${projectId}/invoices`).catch(() => ({ data: [] }));
      setProjectInvoices(invoicesRes.data || []);

      if (isAgency || isProjectManager) {
        const [teamRes, financesRes, timeEntriesRes] = await Promise.all([
          api.get('/users/team-members'),
          api.get(`/projects/${projectId}/finances`).catch(() => ({ data: null })),
          api.get(`/time/project/${projectId}`).catch(() => ({ data: [] }))
        ]);
        setTeamMembers(teamRes.data);
        setTimeEntries(timeEntriesRes.data || []);
        
        // Only update finances if we got valid data
        if (financesRes.data) {
          setFinances({
            totalCost: financesRes.data.costs?.total || financesRes.data.totalCost || 0,
            teamCost: financesRes.data.costs?.internal_team || financesRes.data.teamCost || 0,
            expensesCost: financesRes.data.costs?.expenses_impacting_margin || financesRes.data.costs?.expenses_total || financesRes.data.expensesCost || 0,
            margin: financesRes.data.margin || 0,
            margin_percentage: financesRes.data.margin_percentage || 0,
            budget: financesRes.data.budget || 0,
            expenses_impacting: financesRes.data.costs?.expenses_impacting_margin || 0
          });
        }
        
        // Fetch project manager details
        if (projectRes.data.account_manager_id) {
          const managerRes = await api.get(`/users/${projectRes.data.account_manager_id}`).catch(() => null);
          if (managerRes?.data) {
            setProjectManager(managerRes.data);
          }
        }
        
        // Fetch assigned team details with availability status
        if (projectRes.data.assigned_team?.length > 0) {
          const teamDetails = teamRes.data.filter(m => projectRes.data.assigned_team.includes(m.id));
          
          // Enrich with availability and chrono status
          const enrichedTeam = await Promise.all(teamDetails.map(async (member) => {
            try {
              // Check for active chrono on this project
              const chronoRes = await api.get('/time-entries/', {
                params: { user_id: member.id, is_running: true }
              }).catch(() => ({ data: [] }));
              
              const activeChronos = chronoRes.data || [];
              const hasChronoOnThisProject = activeChronos.some(c => c.project_id === projectId);
              const hasChronoElsewhere = activeChronos.some(c => c.project_id !== projectId);
              
              // Get availability status
              const availRes = await api.get(`/availability/${member.id}`).catch(() => ({ data: { available: true } }));
              
              return {
                ...member,
                is_available: availRes.data?.available !== false,
                has_active_chrono: hasChronoOnThisProject,
                has_chrono_elsewhere: hasChronoElsewhere
              };
            } catch {
              return { ...member, is_available: true, has_active_chrono: false, has_chrono_elsewhere: false };
            }
          }));
          
          setAssignedTeamDetails(enrichedTeam);
        } else {
          setAssignedTeamDetails([]);
        }
        
        // Fetch project activity
        const activityRes = await api.get(`/projects/${projectId}/activity`).catch(() => ({ data: [] }));
        setProjectActivity(activityRes.data || []);
      }
      
      // Fetch team details for clients (simplified view without sensitive data)
      if (isClient && projectRes.data.assigned_team?.length > 0) {
        try {
          const teamRes = await api.get(`/projects/${projectId}/team-public`);
          setAssignedTeamDetails(teamRes.data || []);
        } catch (e) {
          console.log('Could not fetch team for client');
          setAssignedTeamDetails([]);
        }
      }
      
      // Fetch client wallet - for both client and agency users
      try {
        const walletRes = await api.get('/wallet/');
        if (walletRes.data) {
          setClientWallet(walletRes.data);
        }
      } catch (e) {
        console.log('Could not fetch wallet');
      }
      
      // Fetch tickets related to this project
      try {
        const ticketsRes = await api.get('/tickets/', { params: { project_id: projectId } });
        setProjectTickets(ticketsRes.data || []);
      } catch (e) {
        console.log('Could not fetch tickets');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Erreur lors du chargement du projet');
    } finally {
      setLoading(false);
    }
  };

  const handleSignBrief = async () => {
    try {
      await api.post(`/briefs/${brief.id}/sign`);
      toast.success('Brief signé avec succès !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleValidateBAT = async () => {
    try {
      await api.put(`/projects/${projectId}/validate-bat`);
      toast.success('BAT validé avec succès !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // Edit project functions
  const openEditProjectModal = () => {
    setEditProjectForm({
      name: project?.name || '',
      description: project?.description || '',
      estimated_hours: project?.estimated_hours?.toString() || '',
      deadline: project?.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '',
      status: project?.status || ''
    });
    setShowEditProjectModal(true);
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (editProjectForm.name && editProjectForm.name !== project.name) {
        updateData.name = editProjectForm.name;
      }
      if (editProjectForm.description !== project.description) {
        updateData.description = editProjectForm.description;
      }
      if (editProjectForm.estimated_hours && parseFloat(editProjectForm.estimated_hours) !== project.estimated_hours) {
        updateData.estimated_hours = parseFloat(editProjectForm.estimated_hours);
      }
      if (editProjectForm.deadline) {
        updateData.deadline = editProjectForm.deadline;
      }
      if (editProjectForm.status && editProjectForm.status !== project.status) {
        updateData.status = editProjectForm.status;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('Aucune modification détectée');
        setShowEditProjectModal(false);
        return;
      }

      await api.put(`/projects/${projectId}`, updateData);
      toast.success('Projet mis à jour avec succès !');
      setShowEditProjectModal(false);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEstimation = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}`, {
        estimated_hours: parseFloat(estimation.hours),
        estimation_notes: estimation.notes
      });
      toast.success('Estimation mise à jour !');
      setShowEstimationModal(false);
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Team management functions
  const handleAssignManager = async () => {
    if (!selectedManagerId) {
      toast.error('Veuillez sélectionner un chef de projet');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/assign-manager`, { manager_id: selectedManagerId });
      toast.success('Chef de projet assigné !');
      setShowAssignManagerModal(false);
      setSelectedManagerId('');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTeamMember = async () => {
    if (selectedTeamMemberIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un membre');
      return;
    }
    setSaving(true);
    try {
      // Combine existing team with new selections
      const currentTeam = project.assigned_team || [];
      const newTeam = [...new Set([...currentTeam, ...selectedTeamMemberIds])];
      await api.put(`/projects/${projectId}/assign-team`, { team_member_ids: newTeam });
      toast.success('Équipe mise à jour !');
      setShowAssignTeamModal(false);
      setSelectedTeamMemberIds([]);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignMember = async (memberId) => {
    if (!window.confirm('Retirer ce membre du projet ?')) return;
    try {
      await api.delete(`/projects/${projectId}/unassign/${memberId}`);
      toast.success('Membre retiré du projet');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors du retrait');
    }
  };

  const toggleTeamMemberSelection = (memberId) => {
    setSelectedTeamMemberIds(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Task management functions
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.name.trim()) {
      toast.error('Le nom de la tâche est requis');
      return;
    }
    setSaving(true);
    try {
      await api.post('/tasks/', {
        project_id: projectId,
        name: newTask.name,
        description: newTask.description || null,
        assigned_to: newTask.assigned_to || null,
        estimated_hours: newTask.estimated_hours ? parseFloat(newTask.estimated_hours) : null,
        deadline: newTask.deadline || null
      });
      toast.success('Tâche créée !');
      setShowCreateTaskModal(false);
      setNewTask({ name: '', description: '', assigned_to: '', estimated_hours: '', deadline: '' });
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}/status`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Duplicate a task
  const handleDuplicateTask = async (task) => {
    try {
      const newTask = {
        name: `${task.name} (copie)`,
        description: task.description || '',
        project_id: projectId,
        assigned_to: task.assigned_to,
        estimated_hours: task.estimated_hours || 0,
        priority: task.priority || 'medium',
        task_type: task.task_type || 'production',
        status: 'todo'
      };
      await api.post('/tasks/', newTask);
      toast.success('Tâche dupliquée !');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleAddTaskComment = async (taskId, comment) => {
    try {
      await api.post(`/tasks/${taskId}/comments`, { 
        content: comment
      });
      toast.success('Commentaire ajouté !');
      fetchProjectData();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleSubmitVersion = async (taskId) => {
    const notes = window.prompt('Notes pour cette version (optionnel):');
    if (notes === null) return; // User cancelled
    
    try {
      await api.post(`/tasks/${taskId}/submit-version`, { notes: notes || '' });
      toast.success('Version soumise pour validation client !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    }
  };

  const handleClientResponse = async (taskId, response) => {
    let notes = '';
    if (response === 'rejected' || response === 'accepted_with_reservations') {
      notes = window.prompt(
        response === 'rejected' 
          ? 'Raison du refus:' 
          : 'Vos réserves:'
      );
      if (notes === null) return; // User cancelled
    }
    
    try {
      await api.post(`/tasks/${taskId}/client-response`, { response, notes });
      const labels = {
        accepted: 'Tâche acceptée !',
        rejected: 'Tâche refusée',
        accepted_with_reservations: 'Tâche acceptée avec réserves'
      };
      toast.success(labels[response]);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // NEW: Agency validation (blue check)
  const handleAgencyValidate = async (taskId, approved) => {
    let notes = '';
    if (!approved) {
      notes = window.prompt('Raison de la demande de modification:');
      if (notes === null) return;
    }
    
    try {
      await api.post(`/tasks/${taskId}/agency-validate`, { approved, notes });
      if (approved) {
        toast.success('✓ Tâche validée par l\'agence (check bleu). En attente du client.');
      } else {
        toast.success('Modifications demandées au créatif');
      }
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // NEW: Client validation (green check)
  const handleClientValidate = async (taskId, approved, onBehalfOfClient = false) => {
    let notes = '';
    if (!approved) {
      notes = window.prompt('Raison du refus:');
      if (notes === null) return;
    }
    
    try {
      await api.post(`/tasks/${taskId}/client-validate`, { 
        approved, 
        notes, 
        on_behalf_of_client: onBehalfOfClient 
      });
      if (approved) {
        toast.success('✓✓ Tâche validée par le client (check vert). Tâche terminée !');
      } else {
        toast.success('Tâche renvoyée pour modification');
      }
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // =============================================
  // GOOGLE DRIVE & FILES MANAGEMENT
  // =============================================
  const handleSaveGoogleDriveUrl = async () => {
    if (!googleDriveUrl.trim()) {
      toast.error('Veuillez entrer une URL');
      return;
    }
    
    try {
      const updateData = { google_drive_url: googleDriveUrl };
      if (googleDrivePassword) {
        updateData.google_drive_password = googleDrivePassword;
      }
      await api.put(`/projects/${projectId}`, updateData);
      toast.success('Lien Google Drive ajouté !');
      setShowGoogleDriveModal(false);
      setGoogleDriveUrl('');
      setGoogleDrivePassword('');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async () => {
    if (!fileToUpload) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await api.post(`/projects/${projectId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Fichier uploadé avec succès !');
      setShowUploadFileModal(false);
      setFileToUpload(null);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    
    try {
      await api.delete(`/projects/${projectId}/files/${fileId}`);
      toast.success('Fichier supprimé');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteTask = async (taskId, taskName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la tâche "${taskName}" ?\n\nCette action est irréversible.`)) return;
    
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Tâche supprimée avec succès');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      brief_pending: 'bg-yellow-100 text-yellow-700',
      brief_signed: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      review: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      draft: 'Brouillon',
      brief_pending: 'Brief en attente',
      brief_signed: 'Brief validé',
      in_progress: 'En cours',
      review: 'En révision',
      delivered: 'Livré',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.draft)}>
        {labels[status] || status}
      </Badge>
    );
  };

  // =============================================
  // GÉNÉRATION DE RAPPORT PDF DE PROJET
  // =============================================
  const generateProjectReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Helper function to format numbers for PDF (avoid locale issues with non-breaking spaces)
    const formatPdfNumber = (num) => {
      if (typeof num !== 'number' || isNaN(num)) return '0';
      const rounded = Math.round(num * 100) / 100;
      const parts = rounded.toString().split('.');
      // Add thousand separators with regular spaces (not non-breaking)
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      // Return without decimals if they're .00
      return parts.length > 1 && parts[1] !== '0' ? parts.join(',') : parts[0];
    };
    
    // Header with logo
    doc.setFillColor(26, 54, 93); // Navy
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Add agency logo if available
    try {
      const logoUrl = theme?.logo?.lightUrl || theme?.logo?.darkUrl;
      if (logoUrl && logoUrl.startsWith('data:image')) {
        // Logo base64 disponible - l'ajouter au PDF
        doc.addImage(logoUrl, 'PNG', 10, 8, 24, 24);
      } else {
        // Fallback: placeholder texte
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(10, 8, 24, 24, 3, 3, 'F');
        doc.setFontSize(8);
        doc.setTextColor(26, 54, 93);
        doc.text('MSC', 14, 22);
        doc.text('L\'AGENCE', 11, 28);
      }
    } catch (e) {
      // Logo loading failed, continue without it
      console.error('Erreur lors du chargement du logo:', e);
    }
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DE PROJET', pageWidth / 2 + 10, 18, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(project.name || 'Sans nom', pageWidth / 2 + 10, 30, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    let yPos = 55;
    
    // Project Info Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Informations générales', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const projectInfo = [
      ['Client', project.client_name || 'N/A'],
      ['Type', projectTypeLabels[project.project_type] || project.project_type],
      ['Statut', statusConfig[project.status]?.label || project.status],
      ['Date de création', project.created_at ? new Date(project.created_at).toLocaleDateString('fr-FR') : 'N/A'],
      ['Date de livraison', project.deadline ? new Date(project.deadline).toLocaleDateString('fr-FR') : 'N/A'],
    ];
    
    projectInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 60, yPos);
      yPos += 6;
    });
    
    yPos += 10;
    
    // Financial Summary
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Résumé financier', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    const financialInfo = [
      ['Heures estimées', `${project.estimated_hours || 0}h`],
      ['Heures réelles', `${(project.actual_hours || 0).toFixed(1)}h`],
      ['Taux horaire', `${project.hourly_rate || 75}€/h`],
      ['Budget', `${formatPdfNumber(calculateBudget(project.estimated_hours || 0))}€`],
      ['Coût réel', `${formatPdfNumber(finances.totalCost)}€`],
      ['Marge', `${formatPdfNumber(finances.margin)}€ (${finances.margin_percentage.toFixed(1)}%)`],
    ];
    
    financialInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 60, yPos);
      yPos += 6;
    });
    
    yPos += 10;
    
    // Tasks Table
    if (tasks.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Tâches du projet', 14, yPos);
      yPos += 5;
      
      // Calculate actual hours per task from timeEntries
      const getTaskHours = (taskId) => {
        const taskEntries = timeEntries.filter(entry => entry.task_id === taskId);
        return taskEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      };
      
      const taskData = tasks.map(task => {
        const taskHours = task.actual_hours || getTaskHours(task.id);
        return [
          task.name || task.title || 'Sans titre',
          task.assigned_to_name || task.assignee_name || 'Non assigné',
          task.status === 'completed' ? 'Terminée' : 
          task.status === 'validated' ? 'Validée' :
          task.status === 'in_progress' ? 'En cours' : 
          task.status === 'awaiting_client' ? 'À valider' : 
          task.status === 'todo' ? 'À faire' : task.status,
          `${taskHours.toFixed(1)}h / ${task.estimated_hours || 0}h`,
          task.completed_at ? new Date(task.completed_at).toLocaleDateString('fr-FR') : '-'
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Tâche', 'Responsable', 'Statut', 'Temps', 'Terminée le']],
        body: taskData,
        theme: 'striped',
        headStyles: { fillColor: [26, 54, 93] },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
    }
    
    // Time Entries Table
    if (timeEntries.length > 0 && yPos < 250) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Chronos enregistrés', 14, yPos);
      yPos += 5;
      
      const timeData = timeEntries.slice(0, 10).map(entry => [
        entry.description || 'Sans description',
        entry.user_name || 'N/A',
        `${((entry.duration_minutes || entry.duration || 0) / 60).toFixed(1)}h`,
        entry.created_at ? new Date(entry.created_at).toLocaleDateString('fr-FR') : 
        entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : '-'
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Membre', 'Durée', 'Date']],
        body: timeData,
        theme: 'striped',
        headStyles: { fillColor: [26, 54, 93] },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')} - Page ${i}/${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `rapport_${project.name?.replace(/[^a-z0-9]/gi, '_') || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Rapport PDF généré avec succès');
  };

  // =============================================
  // GÉNÉRATION DE RAPPORT PDF CLIENT (sans infos financières sensibles)
  // =============================================
  const generateClientReportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(26, 54, 93); // Navy
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Add agency logo if available
    try {
      const logoUrl = theme?.logo?.lightUrl || theme?.logo?.darkUrl;
      if (logoUrl && logoUrl.startsWith('data:image')) {
        doc.addImage(logoUrl, 'PNG', 10, 10, 25, 25);
      }
    } catch (e) {
      console.error('Erreur logo PDF:', e);
    }
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DE PROJET', pageWidth / 2 + 10, 18, { align: 'center' });
    doc.setFontSize(14);
    doc.text(project.name || 'Sans nom', pageWidth / 2 + 10, 30, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Récapitulatif client', pageWidth / 2 + 10, 40, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    let yPos = 60;
    
    // Project Info Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 54, 93);
    doc.text('Informations du projet', 14, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const projectInfo = [
      ['Client', project.client_name || 'N/A'],
      ['Type de projet', projectTypeLabels[project.project_type] || project.project_type || 'N/A'],
      ['Statut', statusConfig[project.status]?.label || project.status],
      ['Date de début', project.created_at ? new Date(project.created_at).toLocaleDateString('fr-FR') : 'N/A'],
      ['Date de livraison', project.deadline ? new Date(project.deadline).toLocaleDateString('fr-FR') : 'N/A'],
    ];
    
    projectInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 70, yPos);
      yPos += 7;
    });
    
    yPos += 10;
    
    // Hours Summary (not financial)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 54, 93);
    doc.text('Récapitulatif des heures', 14, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
    
    // Helper function to format numbers for PDF (avoid locale issues with non-breaking spaces)
    const formatPdfNum = (num) => {
      if (typeof num !== 'number' || isNaN(num)) return '0';
      const rounded = Math.round(num * 100) / 100;
      const parts = rounded.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.length > 1 && parts[1] !== '00' ? parts.join(',') : parts[0];
    };
    
    const hoursInfo = [
      ['Heures estimées', `${project.estimated_hours || 0}h`],
      ['Heures réalisées', `${(project.actual_hours || 0).toFixed(1)}h`],
      ['Équivalent budget', `${formatPdfNum(calculateBudget(project.estimated_hours || 0))}€`],
      ['Équivalent consommé', `${formatPdfNum(calculateBudget(project.actual_hours || 0))}€`],
    ];
    
    doc.setFontSize(10);
    hoursInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 70, yPos);
      yPos += 7;
    });
    
    yPos += 10;
    
    // Tasks Summary
    if (tasks && tasks.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 54, 93);
      doc.text('Tâches réalisées', 14, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += 5;
      
      // Calculate actual hours per task from timeEntries
      const getTaskHours = (taskId) => {
        const taskEntries = timeEntries.filter(entry => entry.task_id === taskId);
        return taskEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      };
      
      const taskData = tasks.map(task => {
        const taskHours = task.actual_hours || getTaskHours(task.id);
        return [
          task.name || 'Sans titre',
          task.status === 'validated' ? '✓ Validée' : 
          task.status === 'completed' ? 'Terminée' :
          task.status === 'done' ? 'Terminée' :
          task.status === 'in_progress' ? 'En cours' : 
          task.status === 'awaiting_client' ? 'À valider' :
          task.status === 'todo' ? 'À faire' : task.status,
          `${taskHours.toFixed(1)}h`
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Tâche', 'Statut', 'Heures']],
        body: taskData,
        theme: 'striped',
        headStyles: { fillColor: [26, 54, 93] },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
    }
    
    // Team
    if (assignedTeamDetails && assignedTeamDetails.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 54, 93);
      doc.text('Équipe projet', 14, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += 10;
      
      doc.setFontSize(10);
      assignedTeamDetails.forEach(member => {
        doc.text(`• ${member.first_name} ${member.last_name}`, 20, yPos);
        yPos += 6;
      });
    }
    
    // Google Drive Link
    if (project.google_drive_url) {
      yPos += 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 54, 93);
      doc.text('Dossier des fichiers', 14, yPos);
      doc.setTextColor(0, 0, 255);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      yPos += 8;
      doc.textWithLink('Accéder au dossier Google Drive', 14, yPos, { url: project.google_drive_url });
      doc.setTextColor(0, 0, 0);
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Généré le ${new Date().toLocaleDateString('fr-FR')} - MSC Manager - Page ${i}/${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `rapport_client_${project.name?.replace(/[^a-z0-9]/gi, '_') || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Rapport client PDF généré avec succès');
  };

  const projectTypeLabels = {
    logo: 'Création de Logo',
    visual: 'Création Visuelle',
    video: 'Production Vidéo',
    website: 'Site Internet',
    mobile_app: 'Application Mobile',
  };

  const projectTypeIcons = {
    logo: '🎨',
    visual: '🖼️',
    video: '🎬',
    website: '🌐',
    mobile_app: '📱',
  };

  const calculateBudget = (hours) => {
    const hourlyRate = project?.hourly_rate || 75;
    return hours * hourlyRate;
  };

  // Send payment request for a specific installment
  const sendPaymentRequest = async (installmentNumber) => {
    setSendingPaymentRequest(true);
    try {
      await api.post(`/projects/${projectId}/payment-schedule/send-request`, {
        installment_number: installmentNumber
      });
      toast.success('Demande de paiement envoyée au client');
      fetchProjectData();
    } catch (error) {
      console.error('Error sending payment request:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingPaymentRequest(false);
    }
  };

  const progressPercent = project?.estimated_hours > 0 
    ? Math.min((project.actual_hours / project.estimated_hours) * 100, 100) 
    : 0;

  // Open brief edit with existing data
  const openBriefEditor = () => {
    // Initialize edit data from existing brief dynamic_fields or other fields
    const initialData = {
      description: brief?.description || brief?.objective || project?.description || '',
      ...brief?.dynamic_fields,
      // Include other structured data
      inspiration_links: brief?.inspiration_links || [],
      site_structure: brief?.pages || [],
      deliverables: brief?.content_requirements?.map(c => ({
        id: c.id,
        label: c.label,
        checked: true
      })) || []
    };
    setEditBriefData(initialData);
    
    // Load existing audio if any
    if (brief?.audio_url) {
      setEditAudioUrl(brief.audio_url);
      setEditAudioTranscript(brief.audio_transcript || '');
    } else {
      setEditAudioUrl(null);
      setEditAudioTranscript('');
    }
    setEditAudioBlob(null);
    
    setShowBriefForm(true);
  };

  // Audio recording functions for brief edit
  const startEditRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Votre navigateur ne supporte pas l\'enregistrement audio');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      editMediaRecorderRef.current = new MediaRecorder(stream);
      editChunksRef.current = [];
      
      editMediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) editChunksRef.current.push(e.data);
      };
      
      editMediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(editChunksRef.current, { type: 'audio/webm' });
        setEditAudioBlob(blob);
        setEditAudioUrl(URL.createObjectURL(blob));
        await transcribeEditAudio(blob);
      };
      
      editMediaRecorderRef.current.start();
      setIsRecordingEdit(true);
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Erreur lors de l\'accès au microphone');
    }
  };

  const stopEditRecording = () => {
    if (editMediaRecorderRef.current && isRecordingEdit) {
      editMediaRecorderRef.current.stop();
      editMediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecordingEdit(false);
    }
  };

  const transcribeEditAudio = async (blob) => {
    setIsTranscribingEdit(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      const response = await api.post('/briefs/transcribe-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditAudioTranscript(response.data.transcription || response.data.transcript || '');
      toast.success('Audio transcrit !');
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribingEdit(false);
    }
  };

  const deleteEditAudio = () => {
    if (editAudioUrl && editAudioBlob) {
      URL.revokeObjectURL(editAudioUrl);
    }
    setEditAudioBlob(null);
    setEditAudioUrl(null);
    setEditAudioTranscript('');
  };

  // Save edited brief
  const saveBriefChanges = async () => {
    setSavingBrief(true);
    try {
      // Upload new audio if recorded
      let audioUrl = brief?.audio_url;
      let audioTranscript = brief?.audio_transcript;
      
      if (editAudioBlob) {
        try {
          const audioFormData = new FormData();
          audioFormData.append('file', editAudioBlob, 'brief_audio.webm');
          const uploadRes = await api.post('/upload', audioFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          audioUrl = uploadRes.data.url || uploadRes.data.file_url;
          audioTranscript = editAudioTranscript;
        } catch (uploadError) {
          console.error('Error uploading audio:', uploadError);
        }
      } else if (editAudioUrl === null) {
        // Audio was deleted
        audioUrl = null;
        audioTranscript = null;
      }
      
      // Build updated brief data
      const updatedBrief = {
        project_id: projectId,
        brief_type: project.project_type,
        site_type: project.project_type,
        objective: editBriefData.description,
        description: editBriefData.description,
        inspiration_links: editBriefData.inspiration_links || [],
        pages: editBriefData.site_structure || editBriefData.sitemap || [],
        total_pages: (editBriefData.site_structure || editBriefData.sitemap || []).reduce(
          (acc, p) => acc + 1 + (p.children?.length || 0), 0
        ),
        content_requirements: (editBriefData.deliverables || []).map(d => ({
          id: d.id,
          label: d.label,
          type: 'deliverable'
        })),
        dynamic_fields: Object.fromEntries(
          Object.entries(editBriefData).filter(([key]) =>
            !['description', 'inspiration_links', 'site_structure', 'sitemap', 'deliverables'].includes(key)
          )
        ),
        audio_url: audioUrl,
        audio_transcript: audioTranscript
      };

      if (brief?.id) {
        // Update existing brief
        await api.put(`/briefs/${brief.id}`, updatedBrief);
        toast.success('Brief mis à jour');
      } else {
        // Create new brief
        await api.post('/briefs/', updatedBrief);
        toast.success('Brief créé');
      }
      
      setShowBriefForm(false);
      fetchProjectData();
    } catch (error) {
      console.error('Error saving brief:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingBrief(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2">Projet non trouvé</h2>
          <Button onClick={() => navigate(-1)}>Retour</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Back Button & Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{projectTypeIcons[project.project_type]}</span>
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                  {project.name}
                </h1>
              </div>
              <div className="flex items-center gap-3 mt-2">
                {getStatusBadge(project.status)}
                <Badge variant="outline">{projectTypeLabels[project.project_type]}</Badge>
                {project.client_name && (
                  <span className="text-sm text-[#64748b] flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {project.client_name}
                  </span>
                )}
              </div>
              {/* Metadata: created_by, updated_at */}
              {(project.created_by_name || project.updated_at) && (
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  {project.created_by_name && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Créé par {project.created_by_name}
                      {project.created_at && ` le ${new Date(project.created_at).toLocaleDateString('fr-FR')}`}
                    </span>
                  )}
                  {project.updated_at && project.updated_by_name && (
                    <span className="flex items-center gap-1">
                      <Edit className="w-3 h-3" />
                      Modifié par {project.updated_by_name} le {new Date(project.updated_at).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {isAgency && (
              <>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm"
                  onClick={generateProjectReport}
                >
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Rapport PDF</span>
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm"
                  onClick={openEditProjectModal}
                >
                  <Edit className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Modifier</span>
                </Button>
              </>
            )}
            {isClient && project.status === 'delivered' && !project.bat_validated && (
              <Button 
                size="sm"
                className="text-xs sm:text-sm"
                onClick={handleValidateBAT}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <CheckCircle2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Valider le BAT</span>
              </Button>
            )}
            {isAgency && project.status === 'draft' && (
              <Button 
                size="sm"
                className="text-xs sm:text-sm"
                onClick={() => openBriefEditor()}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Créer le brief</span>
              </Button>
            )}
            <Button 
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              data-testid="task-chat-button"
              onClick={() => navigate(`/project/${projectId}/chat`)}
            >
              <MessageSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Chat Tâches</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Clock className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Heures</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {project.actual_hours?.toFixed(1) || 0}h / {project.estimated_hours || 0}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget Card - Show hours for clients/creatives, euros for agency */}
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                  {isAgency ? (
                    <DollarSign className="w-5 h-5 text-green-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Budget</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {isAgency 
                      ? `${calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€ HT`
                      : `${project.estimated_hours || 0}h`
                    }
                  </p>
                  {!isAgency && (
                    <p className="text-xs text-[#94a3b8] font-mono">
                      ≈ {calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€ HT
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spent Card - Show hours for clients/creatives, euros for agency */}
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100">
                  {isAgency ? (
                    <DollarSign className="w-5 h-5 text-orange-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-orange-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">{isAgency ? 'Dépensé' : 'Consommé'}</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {isAgency 
                      ? `${(finances?.totalCost || project.spent || 0).toLocaleString('fr-FR')}€`
                      : `${(project.actual_hours || 0).toFixed(1)}h`
                    }
                  </p>
                  {!isAgency && (
                    <p className="text-xs text-[#94a3b8] font-mono">
                      ≈ {calculateBudget(project.actual_hours || 0).toLocaleString('fr-FR')}€
                    </p>
                  )}
                  {isAgency && finances?.expensesCost > 0 && (
                    <p className="text-xs text-orange-500">
                      dont {finances.expensesCost.toLocaleString('fr-FR')}€ dépenses
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Margin Card - Agency Only - Enhanced */}
          {isAgency && finances && (
            <Card className={cn("card col-span-1 md:col-span-2", finances.margin >= 0 ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30")}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center",
                      finances.margin >= 0 ? "bg-emerald-100" : "bg-red-100"
                    )}>
                      <DollarSign className={cn(
                        "w-6 h-6",
                        finances.margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )} />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748b] mb-0.5">
                        <span className="hidden sm:inline">Marge du projet</span>
                        <span className="sm:hidden">Marge</span>
                      </p>
                      <p className={cn(
                        "font-mono font-bold text-lg sm:text-xl",
                        finances.margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {finances.margin >= 0 ? '+' : ''}{finances.margin.toLocaleString('fr-FR')}€
                        <span className="text-xs sm:text-sm ml-1 sm:ml-2 font-normal">({finances.margin_percentage.toFixed(1)}%)</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Margin Calculation Details */}
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Budget</p>
                      <p className="font-semibold text-[#1a365d]">{calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">−</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Coût équipe</p>
                      <p className="font-semibold text-amber-600">{finances.teamCost.toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">−</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Dépenses</p>
                      <p className="font-semibold text-red-500">{finances.expensesCost.toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">=</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Marge</p>
                      <p className={cn("font-bold", finances.margin >= 0 ? "text-emerald-600" : "text-red-600")}>
                        {finances.margin.toLocaleString('fr-FR')}€
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Schedule Card - Show if project has a schedule */}
          {paymentSchedule && isAgency && (
            <Card className="card col-span-1 md:col-span-2 border-indigo-200 bg-indigo-50/30">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-100">
                      <Calendar className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-indigo-600 font-medium mb-0.5">Paiement échelonné</p>
                      <p className="font-bold text-lg" style={{ color: theme.colors?.primary }}>
                        {paymentSchedule.installments} échéances
                      </p>
                      <p className="text-xs text-slate-500">
                        Total : {paymentSchedule.total_amount?.toLocaleString('fr-FR')}€ HT
                        {paymentSchedule.deposit_percent && ` • Acompte ${paymentSchedule.deposit_percent}%`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Schedule progress */}
                  <div className="flex-1 max-w-md">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Progression</span>
                      <span className="font-medium">
                        {paymentSchedule.schedule?.filter(s => s.status === 'paid').length || 0} / {paymentSchedule.installments} payé(s)
                      </span>
                    </div>
                    <Progress 
                      value={((paymentSchedule.schedule?.filter(s => s.status === 'paid').length || 0) / paymentSchedule.installments) * 100} 
                      className="h-2"
                    />
                  </div>
                  
                  {/* Quick actions */}
                  <div className="flex gap-2">
                    {paymentSchedule.schedule?.find(s => s.status === 'pending') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-indigo-300 text-indigo-600 hover:bg-indigo-100"
                        onClick={() => {
                          const nextPending = paymentSchedule.schedule.find(s => s.status === 'pending');
                          if (nextPending) sendPaymentRequest(nextPending.installment_number);
                        }}
                        disabled={sendingPaymentRequest}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Demander paiement
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`/payment-schedules?project=${projectId}`, '_blank')}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Détails
                    </Button>
                  </div>
                </div>
                
                {/* Mini schedule preview */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {paymentSchedule.schedule?.slice(0, 6).map((item, idx) => (
                    <div 
                      key={idx}
                      className={cn(
                        "p-2 rounded-lg text-center text-xs border",
                        item.status === 'paid' 
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : item.status === 'overdue'
                          ? "bg-red-50 border-red-200 text-red-700"
                          : "bg-white border-slate-200"
                      )}
                    >
                      <p className="font-medium">
                        {item.is_deposit ? 'Acompte' : `Éch. ${item.installment_number}`}
                      </p>
                      <p className="font-bold">{item.amount?.toLocaleString('fr-FR')}€</p>
                      <p className="text-[10px] opacity-70">
                        {item.status === 'paid' ? '✓ Payé' : new Date(item.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-blue-100 shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Équipe</p>
                    <p className="font-mono font-bold text-sm sm:text-base" style={{ color: theme.colors?.primary }}>
                      {assignedTeamDetails?.length || 0} membre{(assignedTeamDetails?.length || 0) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                {/* Team member avatars with status indicators */}
                {assignedTeamDetails && assignedTeamDetails.length > 0 && (
                  <div className="flex -space-x-2 ml-auto">
                    {assignedTeamDetails.slice(0, 4).map((member, idx) => {
                      // Determine status color
                      const getStatusInfo = () => {
                        if (member.has_active_chrono) {
                          return { color: 'bg-blue-500', ring: 'ring-2 ring-blue-300', animate: 'animate-pulse' };
                        }
                        if (member.has_chrono_elsewhere) {
                          return { color: 'bg-orange-500', ring: '', animate: '' };
                        }
                        if (!member.is_available) {
                          return { color: 'bg-slate-400', ring: '', animate: '' };
                        }
                        return { color: 'bg-emerald-500', ring: '', animate: '' };
                      };
                      
                      const status = getStatusInfo();
                      
                      return (
                        <div 
                          key={member.id}
                          className={cn(
                            "relative group cursor-pointer",
                            status.ring
                          )}
                        >
                          <div 
                            className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white text-xs font-semibold border-2 border-white shadow-sm transition-transform hover:scale-110 hover:z-10"
                          >
                            {member.avatar_url ? (
                              <img src={member.avatar_url} alt={member.first_name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="text-[10px] sm:text-xs">{member.first_name?.[0]}{member.last_name?.[0]}</span>
                            )}
                          </div>
                          {/* Status indicator light - only show for agency/PM */}
                          {(isAgency || isProjectManager) && (
                            <span 
                              className={cn(
                                "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm",
                                status.color,
                                status.animate
                              )}
                            />
                          )}
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-20 pointer-events-none">
                            <div className="font-medium">{member.first_name} {member.last_name}</div>
                            <div className="text-slate-300 text-[10px]">
                              {isClient ? (
                                member.role_title || 'Équipe'
                              ) : (
                                member.has_active_chrono ? '⏱️ Chrono actif ici' :
                                member.has_chrono_elsewhere ? '🔶 En travail ailleurs' :
                                !member.is_available ? '⚫ Indisponible' :
                                '🟢 Disponible'
                              )}
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                          </div>
                        </div>
                      );
                    })}
                    {assignedTeamDetails.length > 4 && (
                      <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-[10px] sm:text-xs font-semibold border-2 border-white">
                        +{assignedTeamDetails.length - 4}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance & Savings Card - Show for completed/delivered projects or when actual < estimated */}
        {(project?.status === 'completed' || project?.status === 'delivered' || 
          (project?.actual_hours > 0 && project?.actual_hours < (project?.estimated_hours || 0))) && (
          <Card className="card mb-6 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-emerald-100">
                    <TrendingUp className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-800">
                      {project?.status === 'completed' ? '🎉 Projet terminé avec succès !' : '📈 Performance en cours'}
                    </h3>
                    <p className="text-sm text-emerald-600">
                      {(project?.estimated_hours || 0) - (project?.actual_hours || 0) > 0 
                        ? `${((project?.estimated_hours || 0) - (project?.actual_hours || 0)).toFixed(1)}h économisées sur le budget initial`
                        : 'Suivi en temps réel de votre projet'}
                    </p>
                  </div>
                </div>
                
                {/* Savings Stats */}
                <div className="flex items-center gap-6">
                  {/* Time Saved */}
                  {(project?.estimated_hours || 0) > (project?.actual_hours || 0) && (
                    <div className="text-center px-4 py-2 bg-white rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-600 font-medium">Temps économisé</p>
                      <p className="text-xl font-bold text-emerald-700">
                        {((project?.estimated_hours || 0) - (project?.actual_hours || 0)).toFixed(1)}h
                      </p>
                    </div>
                  )}
                  
                  {/* Money Saved (for clients) */}
                  {isClient && (project?.estimated_hours || 0) > (project?.actual_hours || 0) && (
                    <div className="text-center px-4 py-2 bg-white rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-600 font-medium">Économie</p>
                      <p className="text-xl font-bold text-emerald-700">
                        {(((project?.estimated_hours || 0) - (project?.actual_hours || 0)) * (project?.hourly_rate || 75)).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                  )}
                  
                  {/* Efficiency Score */}
                  <div className="text-center px-4 py-2 bg-white rounded-lg border border-emerald-200">
                    <p className="text-xs text-emerald-600 font-medium">Efficacité</p>
                    <p className="text-xl font-bold text-emerald-700">
                      {project?.estimated_hours > 0 
                        ? Math.min(100, Math.round(((project?.estimated_hours - (project?.actual_hours || 0)) / project?.estimated_hours) * 100 + 100))
                        : 100}%
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Brief Alert - Show when client needs to sign */}
        {isClient && brief && !brief.client_signed && (
          <Card className="card mb-6 border-2 border-amber-200 bg-amber-50/50">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-amber-100 shrink-0">
                    <FileText className="w-7 h-7 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1" style={{ color: theme.colors?.primary }}>
                      Brief à valider
                    </h3>
                    <p className="text-sm text-[#64748b] mb-4">
                      Veuillez lire et valider le brief pour démarrer le projet. Le brief détaille les spécifications et le périmètre de votre projet.
                    </p>
                    
                    {/* Brief Summary */}
                    <div className="bg-white rounded-lg p-4 border">
                      <h4 className="font-medium text-sm mb-3" style={{ color: theme.colors?.primary }}>Résumé du projet</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#64748b]">Type : </span>
                          <span className="font-medium">{project.project_type || brief.brief_type || 'Non défini'}</span>
                        </div>
                        <div>
                          <span className="text-[#64748b]">Budget : </span>
                          <span className="font-medium">{((project.estimated_hours || 0) * (project.hourly_rate || 75)).toLocaleString('fr-FR')}€</span>
                        </div>
                        {brief.technology && (
                          <div>
                            <span className="text-[#64748b]">Technologie : </span>
                            <span className="font-medium">{brief.technology}</span>
                          </div>
                        )}
                        {brief.total_pages && (
                          <div>
                            <span className="text-[#64748b]">Pages : </span>
                            <span className="font-medium">{brief.total_pages} pages</span>
                          </div>
                        )}
                      </div>
                      {brief.objective && (
                        <div className="mt-3 pt-3 border-t">
                          <span className="text-[#64748b] text-sm">Objectif : </span>
                          <p className="text-sm mt-1">{brief.objective}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 shrink-0">
                  <Button 
                    onClick={() => {
                      document.getElementById('brief-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{ backgroundColor: theme.colors?.accent }}
                    className="font-semibold"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Lire le brief complet
                  </Button>
                  <p className="text-xs text-center text-[#64748b]">
                    Lecture obligatoire avant signature
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Bar */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>Progression</span>
              <span className="text-sm font-mono text-[#64748b]">{progressPercent.toFixed(0)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardContent>
        </Card>

        {/* Brief Section - Collapsible when signed */}
        <Card id="brief-section" className="card mb-6 border-l-4" style={{ borderLeftColor: theme.colors?.accent }}>
          <CardHeader 
            className={cn(
              "flex flex-row items-center justify-between py-3 bg-slate-50",
              brief?.agency_signed && brief?.client_signed && "cursor-pointer hover:bg-slate-100 transition-colors"
            )}
            onClick={() => {
              if (brief?.agency_signed && brief?.client_signed) {
                setBriefExpanded(!briefExpanded);
              }
            }}
          >
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Brief du projet
              {brief?.agency_signed && brief?.client_signed && (
                <Badge className="bg-green-100 text-green-700 text-xs">Validé ✓</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {isAgency && !brief && (
                <Button 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openBriefEditor();
                  }}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Créer le brief
                </Button>
              )}
              {brief?.agency_signed && brief?.client_signed && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBriefExpanded(!briefExpanded);
                  }}
                >
                  {briefExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          {/* Show content always if not signed, or if expanded when signed */}
          {(!brief?.agency_signed || !brief?.client_signed || briefExpanded) && (
            <CardContent className="pt-4">
              {brief ? (
                <BriefView 
                  brief={brief} 
                  project={project}
                  onSign={handleSignBrief}
                  isAgency={isAgency}
                  isClient={isClient}
                  theme={theme}
                  wallet={clientWallet}
                  compact={true}
                  onBriefUpdate={(updatedBrief) => setBrief(updatedBrief)}
                />
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun brief créé pour ce projet</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Tabs - Without Brief tab */}
        <Tabs defaultValue="tasks" className="space-y-4 md:space-y-6">
          <TabsList className="bg-white border flex-wrap h-auto p-1 gap-1">
            <TabsTrigger value="tasks" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Clock className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Tâches</span>
            </TabsTrigger>
            <TabsTrigger value="files" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Folder className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Fichiers</span>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Activity className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Suivi</span>
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Users className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Équipe</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <MessageSquare className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Messages</span>
            </TabsTrigger>
            <TabsTrigger value="tickets" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Ticket className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Tickets</span>
              {projectTickets.length > 0 && (
                <Badge className="ml-1 bg-orange-100 text-orange-700 text-[10px] px-1.5">{projectTickets.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
              <Receipt className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">Factures</span>
              {projectInvoices.length > 0 && (
                <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-[10px] px-1.5">{projectInvoices.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle className="text-base">Tâches ({tasks.length})</CardTitle>
                {(isAgency || isProjectManager || isTeamMember) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCreateTaskModal(true)}
                    className="h-8"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {tasks.length === 0 ? (
                  <div className="text-center py-8 text-[#64748b]">
                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune tâche</p>
                    {(isAgency || isProjectManager || isTeamMember) && (
                      <Button 
                        variant="link" 
                        size="sm"
                        className="mt-1"
                        onClick={() => setShowCreateTaskModal(true)}
                      >
                        Créer la première tâche
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                          <TableHead className="w-[40%] text-xs font-medium">Tâche</TableHead>
                          <TableHead className="text-xs font-medium hidden sm:table-cell">Assigné</TableHead>
                          {/* Hide time column for clients - agency only */}
                          {isAgency && (
                            <TableHead className="text-xs font-medium text-center hidden md:table-cell">Temps</TableHead>
                          )}
                          <TableHead className="text-xs font-medium text-center hidden sm:table-cell">Version</TableHead>
                          <TableHead className="text-xs font-medium">Statut</TableHead>
                          <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tasks.map((task) => {
                          // Check validation status from boolean fields
                          const isAgencyValidated = task.agency_validated === true;
                          const isClientValidated = task.client_validated === true;
                          const isFullyValidated = isAgencyValidated && isClientValidated;
                          
                          // Determine the display status based on actual validation state
                          const getTaskDisplayStatus = () => {
                            if (task.status === 'completed' || isFullyValidated) {
                              return { label: '✓✓ Terminée', className: 'bg-emerald-100 text-emerald-700' };
                            }
                            if (isAgencyValidated && !isClientValidated) {
                              return { label: '✓ Agence OK', className: 'bg-blue-100 text-blue-700' };
                            }
                            if (task.status === 'submitted') {
                              return { label: 'Soumise', className: 'bg-cyan-100 text-cyan-700' };
                            }
                            if (task.status === 'in_progress') {
                              return { label: 'En cours', className: 'bg-blue-100 text-blue-700' };
                            }
                            if (task.status === 'revision_requested') {
                              return { label: 'Modif demandée', className: 'bg-amber-100 text-amber-700' };
                            }
                            if (task.status === 'rejected') {
                              return { label: 'Rejetée', className: 'bg-red-100 text-red-700' };
                            }
                            if (task.status === 'pending' || task.status === 'todo') {
                              return { label: 'À faire', className: 'bg-slate-100 text-slate-700' };
                            }
                            // Default: show validated if old status but no validation booleans
                            if (task.status === 'validated') {
                              return { label: '✓✓ Validée', className: 'bg-emerald-100 text-emerald-700' };
                            }
                            return { label: 'En attente', className: 'bg-slate-100 text-slate-700' };
                          };
                          
                          const displayStatus = getTaskDisplayStatus();
                          
                          return (
                          <TableRow 
                            key={task.id}
                            className={cn(
                              "cursor-pointer transition-colors",
                              task.status === 'awaiting_client' && "bg-purple-50/30",
                              task.status === 'agency_validated' && "bg-blue-50/30",
                              task.status === 'completed' && "bg-green-50/30",
                              isFullyValidated && "bg-emerald-50/50 opacity-75 hover:opacity-100"
                            )}
                          >
                            <TableCell className="py-2">
                              <div className="min-w-0 flex items-center gap-2">
                                {/* Validation check icons */}
                                <div className="flex items-center gap-0.5 shrink-0">
                                  {/* Blue check - Agency validated */}
                                  <span title={isAgencyValidated ? `Validé par l'agence${task.agency_validated_at ? ' le ' + new Date(task.agency_validated_at).toLocaleDateString('fr-FR') : ''}` : 'En attente validation agence'}>
                                    <CheckCircle2 className={cn(
                                      "w-4 h-4",
                                      isAgencyValidated ? "text-blue-500" : "text-slate-200"
                                    )} />
                                  </span>
                                  {/* Green check - Client validated */}
                                  <span title={isClientValidated ? `Validé par le client${task.client_validated_at ? ' le ' + new Date(task.client_validated_at).toLocaleDateString('fr-FR') : ''}` : 'En attente validation client'}>
                                    <CheckCircle2 className={cn(
                                      "w-4 h-4",
                                      isClientValidated ? "text-emerald-500" : "text-slate-200"
                                    )} />
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span 
                                    className="font-medium text-sm text-slate-800 truncate hover:text-cyan-600 hover:underline cursor-pointer block"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/tasks/${task.id}`);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    {task.name}
                                  </span>
                                  {task.deadline && (
                                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(task.deadline).toLocaleDateString('fr-FR')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 hidden sm:table-cell">
                              <span className="text-sm text-slate-600">{task.assigned_to_name || '-'}</span>
                            </TableCell>
                            {/* Time column - Agency only */}
                            {isAgency && (
                            <TableCell className="py-2 text-center hidden md:table-cell">
                              <span className="text-xs font-mono text-slate-600">
                                {task.actual_hours?.toFixed(1) || 0}h/{task.estimated_hours || 0}h
                              </span>
                            </TableCell>
                            )}
                            <TableCell className="py-2 text-center hidden sm:table-cell">
                              <Badge variant="outline" className="text-[10px] h-5">
                                V{task.current_version || 0}/{task.max_versions || 3}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2">
                              <Badge className={cn("text-[10px] h-5", displayStatus.className)}>
                                {displayStatus.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {/* Chrono launcher for agency and team members - available on all task statuses */}
                                {(isAgency || isProjectManager || isTeamMember) && (
                                  <ChronoLauncher
                                    taskId={task.id}
                                    projectId={projectId}
                                    taskTitle={task.name}
                                    size="sm"
                                    variant="ghost"
                                    showLabel={false}
                                  />
                                )}
                                {/* Edit task button for agency and team - available on all statuses */}
                                {(isAgency || isProjectManager || isTeamMember) && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-500 hover:text-blue-600"
                                    title="Modifier la tâche"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedTask({...task, _addTime: true});
                                    }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                )}
                                
                                {/* Submit for agency validation - for in_progress tasks */}
                                {(isAgency || isProjectManager || isTeamMember) && task.status === 'in_progress' && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-500 hover:text-purple-600"
                                    title="Soumettre pour validation"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubmitVersion(task.id);
                                    }}
                                  >
                                    <Send className="w-3 h-3" />
                                  </Button>
                                )}
                                
                                {/* Agency validation buttons - for submitted tasks (blue check) */}
                                {(isAgency || isProjectManager) && task.status === 'submitted' && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-amber-600 hover:bg-amber-50 text-xs"
                                      title="Demander des modifications"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAgencyValidate(task.id, false);
                                      }}
                                    >
                                      <Edit className="w-3 h-3 mr-1" />
                                      Modif
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                      title="Valider (check bleu)"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAgencyValidate(task.id, true);
                                      }}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Agence ✓
                                    </Button>
                                  </div>
                                )}
                                
                                {/* Client validation buttons - for agency_validated tasks (green check) */}
                                {isClient && task.status === 'agency_validated' && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-amber-600 hover:bg-amber-50 text-xs"
                                      title="Demander une modification"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientValidate(task.id, false);
                                      }}
                                    >
                                      <Edit className="w-3 h-3 mr-1" />
                                      Modif
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                      title="Valider (check vert)"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientValidate(task.id, true);
                                      }}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Client ✓
                                    </Button>
                                  </div>
                                )}
                                
                                {/* Agency can also validate on behalf of client */}
                                {(isAgency || isProjectManager) && task.status === 'agency_validated' && (
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                    title="Valider pour le client (check vert)"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (confirm('Valider cette tâche pour le compte du client ?')) {
                                        handleClientValidate(task.id, true, true);
                                      }
                                    }}
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Client ✓
                                  </Button>
                                )}
                                
                                {/* Legacy: Client validation buttons - Only show for tasks awaiting validation */}
                                {isClient && task.status === 'awaiting_client' && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-amber-600 hover:bg-amber-50 text-xs"
                                      title="Demander une modification"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTask(task);
                                      }}
                                    >
                                      <Edit className="w-3 h-3 mr-1" />
                                      Modif
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                      title="Valider la tâche"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientResponse(task.id, 'accepted');
                                      }}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Valider
                                    </Button>
                                  </div>
                                )}
                                
                                {/* Client view button for validated/completed tasks - read only */}
                                {isClient && (task.status === 'validated' || task.status === 'completed') && (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Validée
                                  </Badge>
                                )}
                                
                                {/* Client view for other tasks - eye icon + comment button */}
                                {isClient && !['awaiting_client', 'agency_validated', 'validated', 'completed'].includes(task.status) && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-slate-400 hover:text-blue-600"
                                      title="Ajouter un commentaire"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const comment = prompt('Votre commentaire sur cette tâche :');
                                        if (comment) {
                                          handleAddTaskComment(task.id, comment);
                                        }
                                      }}
                                    >
                                      <MessageSquare className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-slate-400 hover:text-slate-600"
                                      title="Voir les détails"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/tasks/${task.id}`);
                                      }}
                                    >
                                      <Eye className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                                
                                {/* More options dropdown for agency */}
                                {isAgency && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="icon" variant="ghost" className="h-7 w-7">
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-52">
                                      {/* View details */}
                                      <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}`)}>
                                        <ExternalLink className="w-4 h-4 mr-2" /> Voir détails
                                      </DropdownMenuItem>
                                      
                                      {/* Edit - only for agency/PM */}
                                      {(isAgency || isProjectManager) && (
                                        <DropdownMenuItem onClick={() => setSelectedTask(task)}>
                                          <Edit className="w-4 h-4 mr-2" /> Modifier
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {/* Separator */}
                                      <div className="h-px bg-slate-200 my-1" />
                                      
                                      {/* Status changes */}
                                      <p className="px-2 py-1 text-xs text-slate-500 font-medium">Changer le statut</p>
                                      
                                      {task.status !== 'todo' && (
                                        <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'todo')}>
                                          <Clock className="w-4 h-4 mr-2 text-slate-500" /> À faire
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {task.status !== 'in_progress' && (
                                        <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'in_progress')}>
                                          <Play className="w-4 h-4 mr-2 text-blue-500" /> En cours
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {task.status !== 'awaiting_client' && (isAgency || isProjectManager) && (
                                        <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'awaiting_client')}>
                                          <User className="w-4 h-4 mr-2 text-purple-500" /> En attente client
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {task.status !== 'completed' && (
                                        <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'completed')}>
                                          <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> Terminée
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {/* Separator */}
                                      <div className="h-px bg-slate-200 my-1" />
                                      
                                      {/* Duplicate task */}
                                      {(isAgency || isProjectManager) && (
                                        <DropdownMenuItem onClick={() => handleDuplicateTask(task)}>
                                          <Copy className="w-4 h-4 mr-2" /> Dupliquer
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {/* Delete - only for agency/PM */}
                                      {(isAgency || isProjectManager) && (
                                        <DropdownMenuItem 
                                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                          onClick={() => handleDeleteTask(task.id, task.name)}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab - Google Drive & Project Files */}
          <TabsContent value="files">
            <div className="space-y-6">
              {/* Google Drive Link */}
              <Card className="card border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <HardDrive className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                          <Folder className="w-5 h-5 text-blue-500" />
                          Dossier Google Drive
                        </h3>
                        <p className="text-sm text-[#64748b] mt-1">
                          {project.google_drive_url 
                            ? "Accédez à tous les fichiers du projet"
                            : "Aucun dossier Google Drive configuré pour ce projet"
                          }
                        </p>
                      </div>
                    </div>
                    {project.google_drive_url ? (
                      <a 
                        href={project.google_drive_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all hover:shadow-lg"
                      >
                        <span>Ouvrir Google Drive</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : isAgency && (
                      <Button
                        variant="outline"
                        onClick={() => setShowGoogleDriveModal(true)}
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Ajouter un lien
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Project Files */}
              <Card className="card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Folder className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                      Fichiers du projet
                    </CardTitle>
                    <CardDescription>Documents, livrables et ressources partagés</CardDescription>
                  </div>
                  {isAgency && (
                    <Button variant="outline" size="sm" onClick={() => setShowUploadFileModal(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Ajouter un fichier
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {project.files && project.files.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {project.files.map((file, idx) => {
                        const isImage = file.type?.includes('image') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file.name);
                        const fileUrl = file.url?.startsWith('http') ? file.url : `${process.env.REACT_APP_BACKEND_URL}${file.url?.startsWith('/api') ? file.url : `/api${file.url}`}`;
                        
                        return (
                          <div 
                            key={idx}
                            className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                          >
                            {/* Image Thumbnail */}
                            {isImage ? (
                              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                                <div className="h-32 bg-slate-100 flex items-center justify-center overflow-hidden">
                                  <img 
                                    src={fileUrl} 
                                    alt={file.name}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                    }}
                                  />
                                </div>
                              </a>
                            ) : (
                              <div className="h-32 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                                {file.type?.includes('video') ? (
                                  <FileVideo className="w-12 h-12 text-red-400" />
                                ) : file.type?.includes('pdf') ? (
                                  <FileText className="w-12 h-12 text-red-500" />
                                ) : (
                                  <File className="w-12 h-12 text-blue-400" />
                                )}
                              </div>
                            )}
                            
                            {/* File Info */}
                            <div className="p-3">
                              <p className="font-medium text-sm truncate" title={file.name}>{file.name}</p>
                              
                              {/* Task association badge */}
                              {file.task_name && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    {file.task_name}
                                  </Badge>
                                  {file.is_deliverable && (
                                    <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                                      v{file.version || 1}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              <p className="text-xs text-slate-500 mt-1">
                                {file.size ? `${(file.size / 1024).toFixed(1)} Ko` : ''} 
                                {file.uploaded_at ? ` · ${new Date(file.uploaded_at).toLocaleDateString('fr-FR')}` : ''}
                              </p>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                <a 
                                  href={fileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded transition-colors"
                                >
                                  <Download className="w-3 h-3" />
                                  Télécharger
                                </a>
                                {isAgency && (
                                  <button 
                                    onClick={() => handleDeleteFile(file.id)}
                                    className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <Folder className="w-16 h-16 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">Aucun fichier</p>
                      <p className="text-sm mt-1">
                        {project.google_drive_url 
                          ? "Les fichiers sont disponibles sur Google Drive"
                          : "Aucun fichier n'a été ajouté à ce projet"
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Client Report PDF - Only visible when project is completed */}
              {project.status === 'completed' && (
                <Card className="card border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <FileText className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            Rapport de projet
                          </h3>
                          <p className="text-sm text-[#64748b] mt-1">
                            Téléchargez le récapitulatif complet du projet
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => generateClientReportPDF()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger le rapport PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Suivi du projet</CardTitle>
                  <CardDescription>Timeline des activités et livrables</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {projectActivity.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune activité enregistrée</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                    
                    <div className="space-y-4">
                      {projectActivity.map((activity, idx) => {
                        const iconMap = {
                          'folder': Folder,
                          'file-text': FileText,
                          'check-square': CheckCircle2,
                          'send': Send,
                          'message-circle': MessageSquare,
                          'package': Package
                        };
                        const IconComponent = iconMap[activity.icon] || Activity;
                        
                        const colorMap = {
                          'project_created': 'bg-blue-100 text-blue-600',
                          'brief_submitted': 'bg-cyan-100 text-cyan-600',
                          'task_created': 'bg-slate-100 text-slate-600',
                          'version_submitted': 'bg-purple-100 text-purple-600',
                          'client_accepted': 'bg-green-100 text-green-600',
                          'client_rejected': 'bg-red-100 text-red-600',
                          'client_accepted_with_reservations': 'bg-yellow-100 text-yellow-600',
                          'deliverable_added': 'bg-emerald-100 text-emerald-600'
                        };
                        const colorClass = colorMap[activity.type] || 'bg-slate-100 text-slate-600';
                        
                        return (
                          <div key={activity.id} className="relative flex gap-4 pl-2">
                            {/* Icon */}
                            <div className={cn(
                              "relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                              colorClass
                            )}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 pb-4">
                              <div className="bg-white rounded-lg border border-slate-100 p-3 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                                  <div>
                                    <p className="font-medium text-sm text-slate-800">{activity.title}</p>
                                    {activity.user_name && (
                                      <p className="text-xs text-slate-500">
                                        par <span className="font-medium">{activity.user_name}</span>
                                      </p>
                                    )}
                                  </div>
                                  <span className="text-xs text-slate-400 whitespace-nowrap">
                                    {activity.timestamp 
                                      ? new Date(activity.timestamp).toLocaleDateString('fr-FR', { 
                                          day: 'numeric', 
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : '-'
                                    }
                                  </span>
                                </div>
                                {activity.description && (
                                  <p className="text-xs text-slate-500 line-clamp-2">{activity.description}</p>
                                )}
                                {activity.file_url && (
                                  <a 
                                    href={activity.file_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:underline mt-2"
                                  >
                                    <Download className="w-3 h-3" />
                                    Télécharger
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <div className="space-y-6">
              {/* Project Manager Card */}
              <Card className="card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Chef de projet</CardTitle>
                    <CardDescription>Responsable du suivi client et de la coordination</CardDescription>
                  </div>
                  {isAgency && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAssignManagerModal(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {project.account_manager_id ? 'Modifier' : 'Assigner'}
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {projectManager ? (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                        {projectManager.first_name?.[0]}{projectManager.last_name?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">
                          {projectManager.first_name} {projectManager.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{projectManager.email}</p>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700">Chef de projet</Badge>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun chef de projet assigné</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Members Card */}
              <Card className="card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Membres de l'équipe</CardTitle>
                    <CardDescription>Créatifs et collaborateurs sur ce projet</CardDescription>
                  </div>
                  {isAgency && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAssignTeamModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {assignedTeamDetails.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucun membre assigné</p>
                      {isAgency && (
                        <Button 
                          variant="link" 
                          className="mt-2"
                          onClick={() => setShowAssignTeamModal(true)}
                        >
                          Assigner des membres
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {assignedTeamDetails.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm">
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {member.first_name} {member.last_name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {member.team_member_type === 'internal' ? 'Créatif interne' : 
                                 member.team_member_type === 'external' ? 'Créatif externe' : 'Membre'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isAgency && (
                              <Badge variant="outline" className="text-xs">
                                {member.hourly_rate || 0}€/h
                              </Badge>
                            )}
                            {isAgency && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleUnassignMember(member.id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages & Commentaires
                </CardTitle>
                <CardDescription>Échangez avec l'équipe projet</CardDescription>
              </CardHeader>
              <CardContent>
                <ProjectMessagesPanel 
                  projectId={projectId} 
                  theme={theme}
                  user={user}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="w-5 h-5" />
                    Tickets associés ({projectTickets.length})
                  </CardTitle>
                  <CardDescription>Support et demandes liées à ce projet</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/tickets/new?project_id=${projectId}`)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nouveau ticket
                </Button>
              </CardHeader>
              <CardContent>
                {projectTickets.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Aucun ticket pour ce projet</p>
                    <p className="text-xs mt-1">Créez un ticket pour signaler un problème ou une demande</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projectTickets.map((ticket) => {
                      const statusStyles = {
                        open: 'bg-yellow-100 text-yellow-700',
                        in_progress: 'bg-blue-100 text-blue-700',
                        waiting_client: 'bg-orange-100 text-orange-700',
                        resolved: 'bg-green-100 text-green-700',
                        closed: 'bg-slate-100 text-slate-700'
                      };
                      const statusLabels = {
                        open: 'Ouvert',
                        in_progress: 'En cours',
                        waiting_client: 'Attente client',
                        resolved: 'Résolu',
                        closed: 'Fermé'
                      };
                      const priorityStyles = {
                        low: 'border-slate-200',
                        medium: 'border-yellow-200',
                        high: 'border-orange-200',
                        urgent: 'border-red-200'
                      };
                      return (
                        <div 
                          key={ticket.id}
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                          className={cn(
                            "p-3 rounded-lg border-l-4 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors",
                            priorityStyles[ticket.priority] || 'border-slate-200'
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{ticket.subject}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={cn("text-[10px]", statusStyles[ticket.status])}>
                                  {statusLabels[ticket.status] || ticket.status}
                                </Badge>
                                <span className="text-[10px] text-slate-500">
                                  {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                                </span>
                                {ticket.assigned_to_name && (
                                  <span className="text-[10px] text-slate-500">
                                    → {ticket.assigned_to_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle className="text-base">Factures & Reçus ({projectInvoices.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {projectInvoices.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune facture pour ce projet</p>
                    <p className="text-sm mt-1">Les factures et reçus apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projectInvoices.map((invoice) => (
                      <div 
                        key={invoice.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-600' :
                            invoice.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            <Receipt className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{invoice.number || `Facture #${invoice.id.slice(0, 6)}`}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold">{(invoice.amount || 0).toLocaleString('fr-FR')}€</p>
                            <Badge className={
                              invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                              invoice.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {invoice.status === 'paid' ? 'Payée' :
                               invoice.status === 'pending' ? 'En attente' : invoice.status}
                            </Badge>
                          </div>
                          {invoice.pdf_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => window.open(invoice.pdf_url, '_blank')}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Brief Form Dialog - Using Dynamic Form */}
        <Dialog open={showBriefForm} onOpenChange={setShowBriefForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="brief-form-description">
            <DialogHeader>
              <DialogTitle>{brief?.id ? 'Modifier le brief' : 'Créer le brief client'}</DialogTitle>
              <p id="brief-form-description" className="text-sm text-[#64748b]">
                {brief?.id ? 'Modifiez le cahier des charges du projet' : 'Remplissez le cahier des charges pour définir les attentes du projet'}
              </p>
            </DialogHeader>
            
            {/* Dynamic Brief Form - Same as project creation */}
            <div className="space-y-4">
              <DynamicBriefForm
                projectType={project.project_type}
                values={editBriefData}
                onChange={setEditBriefData}
                isAgency={isAgency}
              />
              
              {/* Audio Recording Section */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mic className="w-5 h-5 text-indigo-600" />
                    <span className="font-medium text-indigo-700">Brief audio</span>
                  </div>
                  
                  {!isRecordingEdit && !editAudioUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={startEditRecording}
                      className="border-indigo-300 text-indigo-600 hover:bg-indigo-100"
                    >
                      <Mic className="w-4 h-4 mr-1" />
                      Enregistrer
                    </Button>
                  )}
                  
                  {isRecordingEdit && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={stopEditRecording}
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Arrêter
                    </Button>
                  )}
                </div>
                
                {isRecordingEdit && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded border border-red-200">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-600 text-sm">Enregistrement en cours...</span>
                  </div>
                )}
                
                {isTranscribingEdit && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded border border-amber-200">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                    <span className="text-amber-600 text-sm">Transcription en cours...</span>
                  </div>
                )}
                
                {editAudioUrl && !isRecordingEdit && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <audio src={editAudioUrl} controls className="flex-1 h-10" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={deleteEditAudio}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {editAudioTranscript && (
                      <div className="p-3 bg-white rounded border">
                        <p className="text-xs text-slate-500 mb-1">Transcription :</p>
                        <p className="text-sm text-slate-700 italic">"{editAudioTranscript}"</p>
                      </div>
                    )}
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        deleteEditAudio();
                        startEditRecording();
                      }}
                      className="w-full"
                    >
                      <Mic className="w-4 h-4 mr-1" />
                      Réenregistrer
                    </Button>
                  </div>
                )}
                
                {!editAudioUrl && !isRecordingEdit && (
                  <p className="text-xs text-slate-500">
                    Vous pouvez enregistrer un brief audio qui sera transcrit automatiquement.
                  </p>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBriefForm(false)}
                  disabled={savingBrief}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={saveBriefChanges}
                  disabled={savingBrief}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {savingBrief ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {brief?.id ? 'Mettre à jour' : 'Créer le brief'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Estimation Modal */}
        <Dialog open={showEstimationModal} onOpenChange={setShowEstimationModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Estimation du projet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Heures estimées</Label>
                <Input
                  type="number"
                  value={estimation.hours}
                  onChange={(e) => setEstimation({ ...estimation, hours: e.target.value })}
                  placeholder="Ex: 20"
                  className="input-default"
                />
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: `${theme.colors?.accent}10` }}>
                <p className="text-sm text-[#64748b]">Budget estimé</p>
                <p className="text-2xl font-mono font-bold" style={{ color: theme.colors?.primary }}>
                  {calculateBudget(parseFloat(estimation.hours) || 0).toLocaleString('fr-FR')}€
                </p>
                <p className="text-xs text-[#64748b]">Taux horaire: {project.hourly_rate || 75}€/h</p>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={estimation.notes}
                  onChange={(e) => setEstimation({ ...estimation, notes: e.target.value })}
                  placeholder="Notes sur l'estimation..."
                  className="input-default"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEstimationModal(false)}>Annuler</Button>
              <Button 
                onClick={handleUpdateEstimation}
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Manager Modal */}
        <Dialog open={showAssignManagerModal} onOpenChange={setShowAssignManagerModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assigner un chef de projet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sélectionner le responsable</Label>
                <Select value={selectedManagerId} onValueChange={setSelectedManagerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un membre..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                        {member.team_member_type === 'internal' && ' (Interne)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-slate-500">
                Le chef de projet est responsable du suivi client et de la coordination de l'équipe.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignManagerModal(false)}>Annuler</Button>
              <Button 
                onClick={handleAssignManager}
                disabled={saving || !selectedManagerId}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Assignation...' : 'Assigner'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Team Members Modal */}
        <Dialog open={showAssignTeamModal} onOpenChange={setShowAssignTeamModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ajouter des membres à l'équipe</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Sélectionnez les membres à ajouter au projet.
              </p>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {teamMembers
                  .filter(m => !project?.assigned_team?.includes(m.id))
                  .map((member) => (
                    <div 
                      key={member.id} 
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedTeamMemberIds.includes(member.id) 
                          ? "border-cyan-500 bg-cyan-50" 
                          : "border-slate-200 hover:border-slate-300"
                      )}
                      onClick={() => toggleTeamMemberSelection(member.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded border-2 flex items-center justify-center",
                          selectedTeamMemberIds.includes(member.id) 
                            ? "border-cyan-500 bg-cyan-500" 
                            : "border-slate-300"
                        )}>
                          {selectedTeamMemberIds.includes(member.id) && (
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white text-xs font-semibold">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {member.team_member_type === 'internal' ? 'Interne' : 
                             member.team_member_type === 'external' ? 'Externe' : 'Membre'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                {teamMembers.filter(m => !project?.assigned_team?.includes(m.id)).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Tous les membres sont déjà assignés</p>
                  </div>
                )}
              </div>
              {selectedTeamMemberIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded-lg">
                  <Badge className="bg-cyan-500">{selectedTeamMemberIds.length}</Badge>
                  <span className="text-sm text-cyan-700">membre(s) sélectionné(s)</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAssignTeamModal(false);
                setSelectedTeamMemberIds([]);
              }}>Annuler</Button>
              <Button 
                onClick={handleAssignTeamMember}
                disabled={saving || selectedTeamMemberIds.length === 0}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Ajout...' : `Ajouter ${selectedTeamMemberIds.length > 0 ? `(${selectedTeamMemberIds.length})` : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvelle tâche</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de la tâche *</Label>
                <Input
                  value={newTask.name}
                  onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                  placeholder="Ex: Création maquette Figma"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Détails de la tâche..."
                  className="min-h-[80px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigné à</Label>
                  <Select value={newTask.assigned_to || '_unassigned'} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v === '_unassigned' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_unassigned">Non assigné</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.first_name} {member.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heures estimées</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={newTask.estimated_hours}
                    onChange={(e) => setNewTask({ ...newTask, estimated_hours: e.target.value })}
                    placeholder="Ex: 8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={newTask.deadline}
                  onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateTaskModal(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit"
                  disabled={saving || !newTask.name.trim()}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {saving ? 'Création...' : 'Créer la tâche'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Task Detail Modal - Different view for Client vs Agency/Team */}
        <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {isClient ? (
                  <>
                    <Eye className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Détail de la tâche
                  </>
                ) : (
                  <>
                    <Edit className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Modifier la tâche
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedTask && (
              <>
                {/* CLIENT VIEW - Read-only with validation buttons */}
                {isClient ? (
                  <div className="space-y-6">
                    {/* Task Info */}
                    <div className="p-4 bg-slate-50 rounded-xl space-y-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tâche</p>
                        <h3 className="text-lg font-semibold" style={{ color: theme.colors?.primary }}>
                          {selectedTask.name}
                        </h3>
                      </div>
                      
                      {selectedTask.description && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Description</p>
                          <p className="text-sm text-slate-700">{selectedTask.description}</p>
                        </div>
                      )}
                      
                      {/* Assigned Member */}
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Réalisé par</p>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="font-medium">
                              {selectedTask.assigned_to_name || 
                               (selectedTask.assigned_to && teamMembers.find(m => m.id === selectedTask.assigned_to) 
                                 ? `${teamMembers.find(m => m.id === selectedTask.assigned_to).first_name} ${teamMembers.find(m => m.id === selectedTask.assigned_to).last_name}`
                                 : 'Non assigné')}
                            </span>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Temps passé</p>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                              {(selectedTask.actual_hours || 0).toFixed(1)}h
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Metadata: created_by, updated_at */}
                      {(selectedTask.created_by_name || selectedTask.updated_at) && (
                        <div className="flex items-center gap-4 text-xs text-slate-400 pt-2 border-t">
                          {selectedTask.created_by_name && (
                            <span>
                              Créé par {selectedTask.created_by_name}
                              {selectedTask.created_at && ` le ${new Date(selectedTask.created_at).toLocaleDateString('fr-FR')}`}
                            </span>
                          )}
                          {selectedTask.updated_at && selectedTask.updated_by_name && (
                            <span>
                              • Modifié par {selectedTask.updated_by_name} le {new Date(selectedTask.updated_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Status Badge */}
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm text-slate-600">Statut actuel</span>
                      <Badge className={cn(
                        selectedTask.status === 'done' || selectedTask.status === 'validated' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : selectedTask.status === 'review'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                      )}>
                        {selectedTask.status === 'done' ? 'Terminée' :
                         selectedTask.status === 'validated' ? '✓ Validée' :
                         selectedTask.status === 'review' ? 'En attente validation' :
                         selectedTask.status === 'in_progress' ? 'En cours' : 'À faire'}
                      </Badge>
                    </div>
                    
                    {/* Documents/Deliverables Section */}
                    {selectedTask.deliverables?.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Livrables</p>
                        <div className="space-y-2">
                          {selectedTask.deliverables.map((file, idx) => (
                            <a 
                              key={idx}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 border rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              <Folder className="w-4 h-4 text-blue-500" />
                              <span className="text-sm">{file.name}</span>
                              <Download className="w-4 h-4 ml-auto text-slate-400" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Validation Buttons - Only if task is in review */}
                    {(selectedTask.status === 'review' || selectedTask.status === 'done') && (
                      <div className="pt-4 border-t space-y-3">
                        <p className="text-sm font-medium text-slate-700">Cette tâche nécessite votre validation</p>
                        <div className="flex gap-3">
                          <Button
                            onClick={async () => {
                              try {
                                await api.put(`/tasks/${selectedTask.id}`, {
                                  status: 'validated',
                                  validated_by_client: true,
                                  validated_at: new Date().toISOString()
                                });
                                toast.success('Tâche validée !');
                                setSelectedTask(null);
                                fetchProjectData();
                              } catch (error) {
                                toast.error('Erreur lors de la validation');
                              }
                            }}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Valider la tâche
                          </Button>
                          <Button
                            onClick={async () => {
                              const comment = prompt('Commentaire pour la demande de modification :');
                              if (comment) {
                                try {
                                  await api.put(`/tasks/${selectedTask.id}`, {
                                    status: 'revision_requested',
                                    revision_comment: comment,
                                    revision_requested_at: new Date().toISOString()
                                  });
                                  toast.success('Demande de modification envoyée');
                                  setSelectedTask(null);
                                  fetchProjectData();
                                } catch (error) {
                                  toast.error('Erreur');
                                }
                              }
                            }}
                            variant="outline"
                            className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Demander une modification
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Already validated message */}
                    {selectedTask.status === 'validated' && (
                      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm text-emerald-700 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Cette tâche a été validée
                          {selectedTask.validated_at && (
                            <span className="text-xs text-emerald-500 ml-auto">
                              le {new Date(selectedTask.validated_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setSelectedTask(null)}>
                        Fermer
                      </Button>
                    </DialogFooter>
                  </div>
                ) : (
                  /* AGENCY/TEAM VIEW - Full edit mode */
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await api.put(`/tasks/${selectedTask.id}`, {
                        name: selectedTask.name,
                        description: selectedTask.description,
                        assigned_to: selectedTask.assigned_to || null,
                        estimated_hours: selectedTask.estimated_hours ? parseFloat(selectedTask.estimated_hours) : null,
                        deadline: selectedTask.deadline || null,
                        actual_hours: selectedTask.actual_hours ? parseFloat(selectedTask.actual_hours) : null,
                        status: selectedTask.status
                      });
                      toast.success('Tâche mise à jour !');
                      setSelectedTask(null);
                      fetchProjectData();
                    } catch (error) {
                      toast.error('Erreur lors de la mise à jour');
                    }
                  }} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nom de la tâche *</Label>
                      <Input
                        value={selectedTask.name || ''}
                        onChange={(e) => setSelectedTask({ ...selectedTask, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={selectedTask.description || ''}
                        onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                        className="min-h-[80px]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Assigné à</Label>
                        <Select 
                          value={selectedTask.assigned_to || '_unassigned'} 
                          onValueChange={(v) => setSelectedTask({ ...selectedTask, assigned_to: v === '_unassigned' ? null : v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choisir..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_unassigned">Non assigné</SelectItem>
                            {teamMembers.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.first_name} {member.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Statut</Label>
                        <Select 
                          value={selectedTask.status || 'todo'} 
                          onValueChange={(v) => setSelectedTask({ ...selectedTask, status: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">À faire</SelectItem>
                            <SelectItem value="in_progress">En cours</SelectItem>
                            <SelectItem value="review">En révision (visible client)</SelectItem>
                            <SelectItem value="done">Terminée</SelectItem>
                            <SelectItem value="validated">Validée par client</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Heures estimées</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={selectedTask.estimated_hours || ''}
                          onChange={(e) => setSelectedTask({ ...selectedTask, estimated_hours: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Heures réelles</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.25"
                            min="0"
                            value={formatHoursForInput(selectedTask.actual_hours)}
                            onChange={(e) => setSelectedTask({ ...selectedTask, actual_hours: e.target.value })}
                            placeholder="0.00"
                          />
                          {selectedTask.actual_hours > 0 && (
                            <span className="text-sm text-slate-500 whitespace-nowrap">
                              = {formatHoursToHM(selectedTask.actual_hours)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Deadline</Label>
                        <Input
                          type="date"
                          value={selectedTask.deadline?.split('T')[0] || ''}
                          onChange={(e) => setSelectedTask({ ...selectedTask, deadline: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    {/* Client Validation Info */}
                    {selectedTask.validated_by_client && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm text-emerald-700">
                          ✓ Validée par le client
                          {selectedTask.validated_at && ` le ${new Date(selectedTask.validated_at).toLocaleDateString('fr-FR')}`}
                        </p>
                      </div>
                    )}
                    
                    {selectedTask.revision_comment && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-600 font-medium mb-1">Demande de modification du client :</p>
                        <p className="text-sm text-amber-800">{selectedTask.revision_comment}</p>
                      </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => setSelectedTask(null)}>
                        Annuler
                      </Button>
                      <Button type="submit" style={{ backgroundColor: theme.colors?.accent }}>
                        Enregistrer
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Project Modal - Enhanced with brief access */}
        <Dialog open={showEditProjectModal} onOpenChange={setShowEditProjectModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Modifier le projet
              </DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="general">Général</TabsTrigger>
                <TabsTrigger value="brief">Brief</TabsTrigger>
                <TabsTrigger value="budget">Budget</TabsTrigger>
              </TabsList>
              
              {/* General Tab */}
              <TabsContent value="general" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="project_name">Nom du projet *</Label>
                  <Input
                    id="project_name"
                    value={editProjectForm.name}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
                    placeholder="Nom du projet"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project_description">Description</Label>
                  <Textarea
                    id="project_description"
                    value={editProjectForm.description}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                    placeholder="Description du projet..."
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project_deadline">Deadline</Label>
                    <Input
                      id="project_deadline"
                      type="date"
                      value={editProjectForm.deadline}
                      onChange={(e) => setEditProjectForm({ ...editProjectForm, deadline: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="project_status">Statut</Label>
                    <Select 
                      value={editProjectForm.status} 
                      onValueChange={(value) => setEditProjectForm({ ...editProjectForm, status: value })}
                    >
                      <SelectTrigger id="project_status">
                        <SelectValue placeholder="Sélectionner un statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Brouillon</SelectItem>
                        <SelectItem value="pending_brief">En attente de brief</SelectItem>
                        <SelectItem value="brief_sent">Brief envoyé</SelectItem>
                        <SelectItem value="brief_signed">Brief signé</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="review">En revue</SelectItem>
                        <SelectItem value="delivered">Livré</SelectItem>
                        <SelectItem value="completed">Terminé</SelectItem>
                        <SelectItem value="on_hold">En pause</SelectItem>
                        <SelectItem value="cancelled">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
              
              {/* Brief Tab */}
              <TabsContent value="brief" className="space-y-4">
                {brief ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-700">Brief existant</span>
                        </div>
                        <Badge className={brief.agency_signed && brief.client_signed ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                          {brief.agency_signed && brief.client_signed ? 'Validé' : 'En attente de signatures'}
                        </Badge>
                      </div>
                      <p className="text-sm text-green-600 mt-2">
                        Type: {brief.brief_type === 'website_advanced' ? 'Site Web Avancé' : 'Brief classique'}
                      </p>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setShowEditProjectModal(false);
                        openBriefEditor();
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Voir/Modifier le brief
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-700">Pas de brief</span>
                      </div>
                      <p className="text-sm text-amber-600 mt-2">
                        Créez un brief pour définir les besoins du projet et obtenir la validation du client.
                      </p>
                    </div>
                    
                    <Button 
                      className="w-full"
                      style={{ backgroundColor: theme.colors?.accent }}
                      onClick={() => {
                        setShowEditProjectModal(false);
                        openBriefEditor();
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un brief
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              {/* Budget Tab */}
              <TabsContent value="budget" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project_hours">Heures estimées</Label>
                    <Input
                      id="project_hours"
                      type="number"
                      step="0.5"
                      value={editProjectForm.estimated_hours}
                      onChange={(e) => setEditProjectForm({ ...editProjectForm, estimated_hours: e.target.value })}
                      placeholder="40"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Taux horaire</Label>
                    <div className="flex items-center h-10 px-3 bg-slate-100 rounded-md text-sm">
                      {project?.hourly_rate || 75}€/h
                    </div>
                  </div>
                </div>
                
                {/* Budget preview */}
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-600">Budget calculé</span>
                    <span className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                      {((editProjectForm.estimated_hours || 0) * (project?.hourly_rate || 75)).toLocaleString('fr-FR')}€
                    </span>
                  </div>
                  
                  {isAgency && finances.margin !== 0 && (
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-sm text-slate-600">Marge actuelle</span>
                      <span className={cn(
                        "font-bold",
                        finances.margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {finances.margin.toLocaleString('fr-FR')}€ ({finances.margin_percentage.toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setShowEditProjectModal(false);
                    setShowEstimationModal(true);
                  }}
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Ouvrir l'estimateur avancé
                </Button>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowEditProjectModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveProject} 
                disabled={saving || !editProjectForm.name}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Google Drive */}
        <Dialog open={showGoogleDriveModal} onOpenChange={setShowGoogleDriveModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-blue-500" />
                Ajouter un lien Google Drive
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="googleDriveUrl">URL du dossier Google Drive</Label>
                <Input
                  id="googleDriveUrl"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={googleDriveUrl}
                  onChange={(e) => setGoogleDriveUrl(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Collez le lien de partage de votre dossier Google Drive
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="googleDrivePassword">Mot de passe (optionnel)</Label>
                <Input
                  id="googleDrivePassword"
                  type="password"
                  placeholder="Mot de passe du dossier partagé"
                  value={googleDrivePassword}
                  onChange={(e) => setGoogleDrivePassword(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Si le dossier est protégé par un mot de passe, renseignez-le ici
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGoogleDriveModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveGoogleDriveUrl} disabled={!googleDriveUrl.trim()}>
                <Link2 className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Upload Fichier */}
        <Dialog open={showUploadFileModal} onOpenChange={setShowUploadFileModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-green-500" />
                Ajouter un fichier
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="fileUpload">Sélectionner un fichier</Label>
                <Input
                  id="fileUpload"
                  type="file"
                  onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
                  className="w-full cursor-pointer"
                />
                {fileToUpload && (
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-sm">{fileToUpload.name}</span>
                    <span className="text-xs text-slate-500">
                      ({(fileToUpload.size / 1024).toFixed(1)} Ko)
                    </span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowUploadFileModal(false);
                setFileToUpload(null);
              }}>
                Annuler
              </Button>
              <Button 
                onClick={handleFileUpload} 
                disabled={!fileToUpload || uploadingFile}
              >
                {uploadingFile ? (
                  <>Envoi en cours...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Project Chat */}
        <ProjectChat 
          projectId={projectId}
          projectName={project?.name || 'Projet'}
          isOpen={showChat}
          onOpenChange={setShowChat}
        />
      </main>
    </div>
  );
};

export default ProjectDetailPage;
